"use client";

import { useTheme } from "../../app/ThemeProvider";
import { Toaster as Sonner, ToasterProps } from "sonner@2.0.3";
import { useEffect, useState } from "react";

// Detect native iOS shell via the data attribute the Swift shell injects.
// Avoids importing the full NativeBridge service into a shared UI primitive.
function isNativeIOS(): boolean {
  return document.documentElement.getAttribute('data-quantis-native-platform') === 'ios';
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const [nativeIOS, setNativeIOS] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setNativeIOS(isNativeIOS());
    // Re-check if the attribute is set asynchronously by the shell bootstrap.
    const observer = new MutationObserver(() => setNativeIOS(isNativeIOS()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-quantis-native-platform'] });
    return () => observer.disconnect();
  }, []);

  // Native iOS retains its notification-banner treatment. The web/PWA mobile
  // shell follows Android Material: app feedback is a snackbar at the bottom,
  // immediately above the NavigationBar. Desktop stays bottom-right.
  const topAligned = nativeIOS;
  const androidMobile = isMobile && !nativeIOS;
  const position = topAligned ? 'top-center' : androidMobile ? 'bottom-center' : 'bottom-right';
  // Push the banner below the top chrome (native nav bar / mobile floating
  // header), which are both ~52px tall above the safe-area inset. We must use a
  // :root-scoped var: sonner portals the toaster to document.body, OUTSIDE the
  // AppLayout subtree, so --qs-native-header-clearance (set on that subtree)
  // resolves to 0 here — only --safe-area-top (defined on :root) is reachable.
  // And we must feed BOTH `offset` and `mobileOffset`: sonner reads --offset
  // above its 600px breakpoint and --mobile-offset below it, and the iPhone
  // webview is <600px, so it was silently using the 16px mobile default and
  // tucking the toast behind the nav bar.
  const topClearance = 'calc(var(--safe-area-top, 0px) + 56px)';
  // Same portal problem at the bottom: the bar's geometry is overridden on the
  // shell root (80px content zone on mobile web/PWA, not the 55px :root
  // default), which is unreachable from document.body, so composing the height
  // from those defaults here parked the snackbar inside the tab row. AppLayout
  // republishes the composed height on :root for exactly this; the fallback
  // covers the pre-shell case (login, first paint), where 0px would be wrong
  // if a toast beats the shell to the screen.
  const bottomNavClearance =
    'calc(var(--qs-mobile-bottom-nav-height, calc(var(--qs-mobile-bottom-nav-min-height, 55px) + var(--qs-mobile-bottom-nav-safe-floor, max(var(--safe-area-bottom, 0px), 34px)))) + 8px)';

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group font-apple"
      position={position}
      offset={topAligned ? topClearance : undefined}
      mobileOffset={
        topAligned
          ? { top: topClearance, left: '16px', right: '16px' }
          : androidMobile
            ? { bottom: bottomNavClearance, left: '16px', right: '16px' }
            : undefined
      }
      toastOptions={{
        style: {
          fontFamily: 'var(--font-apple)',
          fontSize: 15,
          lineHeight: isMobile ? '18px' : '1.4',
          letterSpacing: isMobile ? '-0.23px' : 'normal',
          fontWeight: 400,
          borderRadius: androidMobile ? 8 : isMobile ? 22 : 'var(--radius-popover)',
          // Adaptive frosted banner (visible on any background — the old 7%
          // white + white text vanished on the white notification list). Dark
          // text on light material in light mode, inverse in dark mode.
          background: androidMobile
            ? 'color-mix(in srgb, var(--foreground) 92%, var(--background))'
            : isMobile
            ? 'color-mix(in srgb, var(--popover) 86%, transparent)'
            : 'var(--popover)',
          color: androidMobile ? 'var(--background)' : 'var(--popover-foreground)',
          border: androidMobile
            ? 'none'
            : isMobile
            ? '1px solid color-mix(in srgb, var(--border) 55%, transparent)'
            : '1px solid var(--border)',
          boxShadow: androidMobile
            ? '0 -2px 10px rgba(0, 0, 0, 0.14), 0 4px 12px rgba(0, 0, 0, 0.22)'
            : isMobile ? '0 12px 32px rgba(0, 0, 0, 0.18)' : 'var(--elevation-sm)',
          backdropFilter: isMobile && !androidMobile ? 'blur(30px) saturate(180%)' : 'none',
          WebkitBackdropFilter: isMobile && !androidMobile ? 'blur(30px) saturate(180%)' : 'none',
          padding: androidMobile ? '14px 16px' : isMobile ? '13px 17px 13px 14px' : '12px 16px',
          gap: androidMobile ? 12 : isMobile ? 10 : 8,
          // Vertical placement + side gutters are owned by the Toaster
          // `offset`/`mobileOffset` props above; here we only cap the width so
          // the banner doesn't stretch edge-to-edge on wide screens.
          ...(isMobile ? { maxWidth: 'min(calc(100vw - 32px), 420px)', width: '100%' } : {}),
        },
        className: androidMobile ? 'android-snackbar' : isMobile ? 'ios-notification' : '',
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
